import api from './api';

export interface HasHistoryResponse {
  hasHistory: boolean;
}

export interface Assinatura {
  id: string;
  company_id: number;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'CANCELED' | 'EXPIRED';
  billing_cycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  wallet_amount: number;
  is_unlimited_wallet: boolean;
  started_at: string;
  expires_at: string | null;
  canceled_at: string | null;
  plan_id: number | null;
  plan: {
    id: number;
    name: string;
    slug: string;
    features?: Array<{
      id: number;
      feature_key: string | null;
      exame_id: number | null;
      included: boolean;
      monthly_limit: number | null;
      extra_cost_per_exam: number | null;
      feature_name?: string;
      feature_description?: string | null;
      exame?: {
        id: number;
        nome: string;
      } | null;
    }>;
    price_brackets?: Array<{
      id: number;
      min_lives: number;
      max_lives: number | null;
      price_per_life_monthly: number;
    }>;
  } | null;
  current_bracket_id: number | null;
  current_bracket: {
    id: number;
    min_lives: number;
    max_lives: number | null;
    price_per_life_monthly: number;
  } | null;
  contracted_lives: number;
  extra_life_price: number | null;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  id: number;
  subscription_id: string;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  base_amount: number;
  extra_lives_amount: number;
  extra_exams_amount: number;
  total_amount: number;
  status: string;
  paid_at: string | null;
  items: InvoiceItem[];
  created_at: string;
}

export interface InvoiceItem {
  id: number;
  invoice_id: number;
  description: string;
  exame_id: number | null;
  exame_agendamento_id: number | null;
  colaborador_id: number | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  type: string;
  colaborador?: {
    id: number;
    nome: string;
    cpf: string;
    data_admissao: string | null;
    cargo: {
      id: number;
      nome: string;
    } | null;
    setor: {
      id: number;
      nome: string;
    } | null;
  } | null;
  exame_agendamento?: {
    id: number;
    data_agendamento: string;
    exame: {
      id: number;
      nome: string;
    } | null;
    exame_clinico: {
      id: number;
      nome: string;
    } | null;
    variacao: {
      id: number;
      nome: string;
    } | null;
    clinica: {
      id: number;
      nome: string;
    } | null;
  } | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ListAssinaturasParams {
  page?: number;
  limit?: number;
}

export interface ListInvoicesParams {
  page?: number;
  limit?: number;
}

export const assinaturasService = {
  async hasHistory(): Promise<boolean> {
    const response = await api.get<HasHistoryResponse>('/assinaturas/has-history');
    return response.data.hasHistory;
  },

  async findAll(params?: ListAssinaturasParams): Promise<PaginatedResponse<Assinatura>> {
    // A API espera strings numéricas na query string, não números
    // O axios serializa números corretamente, mas vamos garantir que sejam strings
    const queryParams: Record<string, string> = {};
    if (params?.limit !== undefined) {
      queryParams.limit = String(params.limit);
    }
    if (params?.page !== undefined) {
      queryParams.page = String(params.page);
    }

    const response = await api.get<PaginatedResponse<Assinatura>>('/assinaturas', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined
    });
    return response.data;
  },

  async findOne(id: string): Promise<Assinatura> {
    const response = await api.get<Assinatura>(`/assinaturas/${id}`);
    return response.data;
  },

  async getInvoices(subscriptionId: string, params?: ListInvoicesParams): Promise<PaginatedResponse<Invoice>> {
    const response = await api.get<PaginatedResponse<Invoice>>(`/assinaturas/${subscriptionId}/invoices`, { params });
    return response.data;
  },

  async getInvoice(subscriptionId: string, invoiceId: number): Promise<Invoice & { subscription?: any }> {
    const response = await api.get<Invoice & { subscription?: any }>(`/assinaturas/${subscriptionId}/invoices/${invoiceId}`);
    return response.data;
  },

  async downloadInvoicePDF(subscriptionId: string, invoiceId: number): Promise<Blob> {
    const response = await api.get(`/assinaturas/${subscriptionId}/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async upgradeBracket(subscriptionId: string, newBracketId: number): Promise<Assinatura> {
    const response = await api.patch<Assinatura>(`/assinaturas/${subscriptionId}/upgrade-bracket`, {
      new_bracket_id: newBracketId,
    });
    return response.data;
  },
};

